import { cache, loadModule } from '@weareduet/core';

import Modal from '@/globals/Modal';
import FadeIn, { fadeInWithin } from '@/globals/FadeIn';

import '@/plugins/logger';
import webfontloader from '@/plugins/webfontloader';
import activeLinks from '@/utils/activeLinks';
import localLinks from '@/utils/localLinks';
import {
	destroyBehaviour,
	destroyBehavioursWithin,
	destroyModule,
	hydrateBehaviours,
} from '@/utils/sponLifecycle';
import { showMessage, hideMessage } from './ui/messages';

const globalModuleIds = [];

function mountGlobalModule(config) {
	if (cache.has(config.id)) return;

	globalModuleIds.push(config.id);
	loadModule(config);
}

webfontloader();

if (import.meta.env.DEV) {
	document.documentElement.classList.add('is-dev');
}

// load from data-behaviours
hydrateBehaviours(document.body);

mountGlobalModule({
	module: Modal,
	id: 'modal',
	node: document.body,
	keepAlive: true,
});

mountGlobalModule({
	module: FadeIn,
	id: 'fadein',
	node: document.body,
	keepAlive: true,
});

activeLinks(window.location.pathname);
localLinks();

/*
 * Sprig / htmx lifecycle (htmx 2.x, shipped by Sprig).
 *
 * Teardown is tied to REMOVAL, not to the request. `htmx:beforeSwap` fires for
 * every response — including 204 and 4xx/5xx, where htmx's default
 * responseHandling does not swap (`detail.shouldSwap === false`) — and any
 * later listener may still cancel the swap. Tearing down there left the
 * retained content inert after a failed request. htmx fires
 * `htmx:beforeCleanupElement` on every element it is about to remove (each
 * removed child for innerHTML, the target itself for outerHTML, OOB and
 * delete swaps), synchronously, still attached, and only when a swap
 * actually runs — so behaviours are destroyed exactly when their node goes.
 *
 * The loading state settles on `htmx:afterRequest`, which htmx fires on every
 * terminal outcome (success, 204, error status, network error, abort,
 * timeout), plus `htmx:onLoadError` for a response handler that threw. It is
 * counted per request, so one request finishing does not clear the state
 * while another is still in flight.
 */
const pendingRequests = new Set();

function setResultsLoading(isLoading) {
	const results = document.getElementById('results');
	if (results) {
		results.classList.toggle('is-loading', isLoading);
	}
}

function handleBeforeSend(event) {
	hideMessage();

	const xhr = event?.detail?.xhr;
	if (xhr) pendingRequests.add(xhr);
	setResultsLoading(true);
}

function handleRequestEnd(event) {
	const xhr = event?.detail?.xhr;
	if (xhr) {
		pendingRequests.delete(xhr);
	} else {
		pendingRequests.clear();
	}

	if (pendingRequests.size === 0) setResultsLoading(false);
}

function handleBeforeSwap(event) {
	const { target, detail } = event ?? {};
	if (!target || !detail?.shouldSwap) return;

	const triggeringEvent = detail.requestConfig?.triggeringEvent;
	if (!triggeringEvent) return;

	const srcElement =
		triggeringEvent.srcElement ?? triggeringEvent.target ?? null;
	if (!srcElement?.closest) return;

	if (srcElement.closest('.c-pagination')) {
		window.scrollTo({
			top: target.getBoundingClientRect().top + window.scrollY - 100,
			behavior: 'smooth',
		});
	}
}

function handleBeforeCleanupElement({ target }) {
	// Fired for every element in a removed subtree, text nodes included; only
	// behaviour roots need work, and each gets its own event.
	if (!(target instanceof Element) || !target.hasAttribute('data-behaviour')) {
		return;
	}

	destroyBehaviour(target);
}

function handleAfterSwap({ target }) {
	// An innerHTML/append swap keeps the target node itself, so a behaviour
	// rooted ON the target survives with references to content that has just
	// been replaced. Re-mount it against the new content (hydrate below).
	destroyBehaviour(target);

	localLinks(target);
	activeLinks(window.location.pathname);
	hydrateBehaviours(target);

	// `FadeIn` is a keepAlive global, so hydrateBehaviours never reaches it.
	// Hand the swapped-in nodes to the live observer or they stay invisible.
	fadeInWithin(target);
}

function handleResponseError() {
	showMessage('Something went wrong, please try again.');
}

const htmxHandlers = {
	'htmx:beforeSend': handleBeforeSend,
	// Scroll to the top of the `sprig` container when paginating.
	'htmx:beforeSwap': handleBeforeSwap,
	'htmx:beforeCleanupElement': handleBeforeCleanupElement,
	'htmx:afterSwap': handleAfterSwap,
	'htmx:afterRequest': handleRequestEnd,
	'htmx:onLoadError': handleRequestEnd,
	'htmx:responseError': handleResponseError,
};

// https://htmx.org/events
if (typeof htmx !== 'undefined') {
	Object.entries(htmxHandlers).forEach(([name, handler]) =>
		htmx.on(name, handler)
	);
}

if (import.meta.hot) {
	import.meta.hot.dispose(() => {
		if (typeof htmx !== 'undefined' && typeof htmx.off === 'function') {
			Object.entries(htmxHandlers).forEach(([name, handler]) =>
				htmx.off(name, handler)
			);
		}

		destroyBehavioursWithin(document.body);
		globalModuleIds.forEach(id => destroyModule(id));
	});
}
